/**
 * Checklist resolution engine — pure functions for building, evaluating,
 * and snapshotting task checklists.
 *
 * No React dependencies. All functions are deterministic given their inputs.
 */

import type {
  TaskListDefinition,
  TaskSectionDefinition,
  TaskDefinition,
  TaskListReference,
  TaskFieldOverride,
  TaskDropdownDefinition,
  TemplateTaskConfig,
  TaskFieldCondition,
  TaskStatus,
  SavedChecklist,
  SavedSection,
  SavedTask,
  SavedAudit,
} from '@techgitter/contracts';
import { isTaskListReference } from '@techgitter/contracts';
import type {
  LiveChecklist,
  LiveSection,
  LiveTask,
  ChecklistProgress,
} from '../types/taskList';
import type { AdditionalDetailsValues, TemplateCustomerContext, FieldCondition } from '../types/ticketTypeTemplate';
import { evaluateFieldCondition } from './templateEngine';

// ---------------------------------------------------------------------------
// Condition evaluation bridge
// ---------------------------------------------------------------------------

/**
 * Evaluate a TaskFieldCondition. `fieldValue`/`customerContext` delegate to
 * the shared FieldCondition evaluator (TaskFieldCondition is a structural
 * subtype). `taskOutcome` conditions compare another task's live state (its
 * selected outcome or completion) via the `outcomes` context — absent that
 * context (e.g. at initial resolve, before any task is actioned) they
 * evaluate false, so a dependent task stays hidden until its source resolves.
 */
function evaluateTaskCondition(
  condition: TaskFieldCondition | undefined,
  additionalDetails: AdditionalDetailsValues,
  customer: TemplateCustomerContext,
  outcomes?: TaskOutcomeContext,
): boolean {
  if (!condition) return true;
  if (condition.type === 'taskOutcome') return evaluateTaskOutcomeCondition(condition, outcomes);
  return evaluateFieldCondition(condition as FieldCondition, additionalDetails, customer);
}

/** One source task's live state, as read by a `taskOutcome` condition. */
export interface TaskOutcomeState {
  /** The source task's selected dropdown option, if one is chosen. */
  outcome?: string;
  /** Whether the source task is marked done (binary `isTaskDone`). */
  completed: boolean;
}

/** taskId → live outcome/completion. Keyed by *base* taskId (composite
 *  attachment suffixes stripped) so a condition authored against the base id
 *  matches regardless of attachment index. */
export type TaskOutcomeContext = Map<string, TaskOutcomeState>;

/** Strip a composite attachment suffix (`--N`) so a condition authored
 *  against a base taskId still matches when the list was attached with a
 *  non-zero index. */
function baseTaskId(id: string): string {
  const i = id.indexOf(COMPOSITE_SEPARATOR);
  return i === -1 ? id : id.slice(0, i);
}

/**
 * Build the taskId → outcome/completion map from a live checklist, for
 * evaluating `taskOutcome` conditions. Keyed by base taskId. NOTE: if the
 * same task list is attached more than once, later instances overwrite
 * earlier ones — an accepted v1 limitation of same-list task-outcome
 * conditions (they can't distinguish duplicate attachments).
 */
export function buildTaskOutcomeContext(checklist: LiveChecklist): TaskOutcomeContext {
  const map: TaskOutcomeContext = new Map();
  for (const section of checklist.sections) {
    for (const task of section.tasks) {
      map.set(baseTaskId(task.taskId), {
        outcome: task.selectedOption,
        completed: isTaskDone(task),
      });
    }
  }
  return map;
}

/** Compare a source task's outcome/completion against a taskOutcome condition. */
function evaluateTaskOutcomeCondition(
  condition: TaskFieldCondition,
  outcomes: TaskOutcomeContext | undefined,
): boolean {
  if (!outcomes || !condition.taskId) return false;
  const state = outcomes.get(baseTaskId(condition.taskId));
  if (!state) return false;
  const source: string | boolean | undefined =
    (condition.aspect ?? 'outcome') === 'completed' ? state.completed : state.outcome;

  switch (condition.operator) {
    case 'equals':
      return source === condition.value;
    case 'notEquals':
      return source !== condition.value;
    case 'contains':
      return typeof source === 'string' && source.includes(String(condition.value));
    case 'in':
      return Array.isArray(condition.value) && (condition.value as unknown[]).includes(source);
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Composite ID helpers (for duplicate task list attachments)
// ---------------------------------------------------------------------------

const COMPOSITE_SEPARATOR = '--';

function compositeId(baseId: string, index: number): string {
  return index === 0 ? baseId : `${baseId}${COMPOSITE_SEPARATOR}${index}`;
}

// ---------------------------------------------------------------------------
// Override application
// ---------------------------------------------------------------------------

function applyTaskOverrides(
  tasks: TaskDefinition[],
  overrides?: TaskFieldOverride[],
  removeTasks?: string[],
  addTasks?: TaskDefinition[],
): TaskDefinition[] {
  const removeSet = new Set(removeTasks ?? []);
  const overrideMap = new Map((overrides ?? []).map(o => [o.taskId, o]));

  let result = tasks
    .filter(t => !removeSet.has(t.taskId))
    .map(t => {
      const override = overrideMap.get(t.taskId);
      if (!override) return t;

      return {
        ...t,
        ...(override.title !== undefined && { title: override.title }),
        ...(override.description !== undefined && { description: override.description }),
        ...(override.groupLabel !== undefined && { groupLabel: override.groupLabel }),
        ...(override.noteRequired !== undefined && { noteRequired: override.noteRequired }),
        // null explicitly reverts a dropdown task back to a checkbox task
        ...(override.dropdownId !== undefined && {
          dropdownId: override.dropdownId === null ? undefined : override.dropdownId,
        }),
        // null explicitly clears the condition
        showWhen: override.showWhen === null ? undefined : (override.showWhen ?? t.showWhen),
      };
    });

  if (addTasks?.length) {
    result = [...result, ...addTasks];
  }

  return result;
}

function applySectionOverrides(
  sections: TaskSectionDefinition[],
  ref: TaskListReference,
): TaskSectionDefinition[] {
  const removeSet = new Set(ref.removeSections ?? []);
  const overrideMap = new Map((ref.sectionOverrides ?? []).map(o => [o.sectionId, o]));

  let result = sections
    .filter(s => !removeSet.has(s.sectionId))
    .map(s => {
      const override = overrideMap.get(s.sectionId);
      if (!override) return s;

      return {
        ...s,
        ...(override.label !== undefined && { label: override.label }),
        showWhen: override.showWhen === null ? undefined : (override.showWhen ?? s.showWhen),
        tasks: applyTaskOverrides(s.tasks, override.taskOverrides, override.removeTasks, override.addTasks),
      };
    });

  // Apply order overrides
  const orderedOverrides = (ref.sectionOverrides ?? []).filter(o => o.order !== undefined);
  if (orderedOverrides.length > 0) {
    const orderMap = new Map(orderedOverrides.map(o => [o.sectionId, o.order!]));
    result = result.sort((a, b) => {
      const oa = orderMap.get(a.sectionId) ?? Infinity;
      const ob = orderMap.get(b.sectionId) ?? Infinity;
      if (oa === Infinity && ob === Infinity) return 0;
      return oa - ob;
    });
  }

  // Add extra sections
  if (ref.addSections?.length) {
    result = [...result, ...ref.addSections];
  }

  return result;
}

// ---------------------------------------------------------------------------
// Live task/section builders
// ---------------------------------------------------------------------------

function buildLiveTask(
  def: TaskDefinition,
  additionalDetails: AdditionalDetailsValues,
  customer: TemplateCustomerContext,
  dropdownMap: Map<string, string[]>,
): LiveTask {
  const options = def.dropdownId ? (dropdownMap.get(def.dropdownId) ?? []) : undefined;
  return {
    taskId: def.taskId,
    title: def.title,
    description: def.description,
    url: def.url,
    groupLabel: def.groupLabel,
    status: 'new',
    note: '',
    noteRequired: def.noteRequired,
    dropdownId: def.dropdownId,
    options,
    isRemoved: false,
    isVisible: evaluateTaskCondition(def.showWhen, additionalDetails, customer),
    showWhen: def.showWhen,
    source: 'definition',
  };
}

function buildLiveSection(
  def: TaskSectionDefinition,
  sourceTaskListId: string | undefined,
  additionalDetails: AdditionalDetailsValues,
  customer: TemplateCustomerContext,
  dropdownMap: Map<string, string[]>,
  inheritedShowWhens: TaskFieldCondition[] = [],
): LiveSection {
  // Section is visible iff its own showWhen evaluates true AND every
  // condition inherited from enclosing TaskListReferences evaluates
  // true. The inherited conditions are baked into isVisible at resolve
  // time only — reEvaluateVisibility (which sees only the live
  // section's own showWhen) won't re-apply them. Same trade-off as
  // the legacy single-level ref.showWhen handling.
  const ownVisible = evaluateTaskCondition(def.showWhen, additionalDetails, customer);
  const inheritedVisible = inheritedShowWhens.every(c =>
    evaluateTaskCondition(c, additionalDetails, customer),
  );
  return {
    sectionId: def.sectionId,
    label: def.label,
    sourceTaskListId,
    isRemoved: false,
    isVisible: ownVisible && inheritedVisible,
    showWhen: def.showWhen,
    tasks: def.tasks.map(t => buildLiveTask(t, additionalDetails, customer, dropdownMap)),
  };
}

/** dropdownId → options lookup, shared by the resolve entry points. */
function buildDropdownMap(
  taskDropdowns: TaskDropdownDefinition[],
): Map<string, string[]> {
  return new Map(taskDropdowns.map(d => [d.dropdownId, d.options]));
}

// ---------------------------------------------------------------------------
// Reference flattening — shared between resolveChecklist and
// resolveStandaloneTaskList.
//
// A TaskList's sections may include TaskListReferences pointing at
// other TaskLists. The flattener walks the section tree recursively,
// expanding each reference into its target's sections (with the
// reference's overrides applied), and accumulating any ref-level
// showWhens as "inherited" conditions on the resulting flat sections.
//
// Cycles (A → B → A) are detected via a per-call visited set and
// surface as warnings rather than infinite recursion.
// ---------------------------------------------------------------------------

interface FlatEntry {
  def: TaskSectionDefinition;
  /**
   * Conditions from each enclosing TaskListReference on the path that
   * produced this entry. The live section's isVisible is `(own
   * showWhen) AND (all inherited)`. Empty array for top-level inline
   * sections that came in via no reference.
   */
  inheritedShowWhens: TaskFieldCondition[];
  sourceTaskListId: string | undefined;
}

function flattenReferences(
  sections: Array<TaskSectionDefinition | TaskListReference>,
  catalogMap: Map<string, TaskListDefinition>,
  visited: Set<string>,
  ambientShowWhens: TaskFieldCondition[],
  ambientSourceTaskListId: string | undefined,
  warnings: string[],
): FlatEntry[] {
  const result: FlatEntry[] = [];
  for (const entry of sections) {
    if (isTaskListReference(entry)) {
      const ref = entry as TaskListReference;
      if (visited.has(ref.taskListId)) {
        warnings.push(
          `Cycle detected in task list reference "${ref.taskListId}" — skipped`,
        );
        continue;
      }
      const target = catalogMap.get(ref.taskListId);
      if (!target) {
        warnings.push(`Could not display task list "${ref.taskListId}"`);
        continue;
      }
      const nextVisited = new Set(visited);
      nextVisited.add(ref.taskListId);
      // ShowWhens inherited *inside* this reference's target are the
      // ambient set plus this ref's own showWhen (if any).
      const innerShowWhens = ref.showWhen
        ? [...ambientShowWhens, ref.showWhen]
        : ambientShowWhens;
      // Recursively flatten the target first — that gives us a flat
      // list of inline section definitions with their own inherited
      // showWhens already baked in.
      const innerFlat = flattenReferences(
        target.sections,
        catalogMap,
        nextVisited,
        innerShowWhens,
        ref.taskListId,
        warnings,
      );
      // applySectionOverrides operates on plain section defs. We pass
      // the inner flattened defs through it, then reconcile back to
      // FlatEntries by sectionId so inheritedShowWhens / sourceTaskListId
      // are preserved for surviving sections. Sections introduced via
      // `addSections` get the innerShowWhens (they were "added" inside
      // this reference, so they inherit the same showWhens as the
      // reference's target body).
      const innerDefs = innerFlat.map(f => f.def);
      const overridden = applySectionOverrides(innerDefs, ref);
      const innerByIdAndIndex = new Map<string, FlatEntry>();
      innerFlat.forEach(f => innerByIdAndIndex.set(f.def.sectionId, f));
      overridden.forEach((def, idx) => {
        const carryOver = innerByIdAndIndex.get(def.sectionId);
        const finalDef = ref.labelOverride && idx === 0
          ? { ...def, label: ref.labelOverride }
          : def;
        result.push({
          def: finalDef,
          inheritedShowWhens: carryOver
            ? carryOver.inheritedShowWhens
            : innerShowWhens,
          sourceTaskListId: carryOver?.sourceTaskListId ?? ref.taskListId,
        });
      });
    } else {
      // Inline section — passes through with ambient inheritance.
      result.push({
        def: entry as TaskSectionDefinition,
        inheritedShowWhens: ambientShowWhens,
        sourceTaskListId: ambientSourceTaskListId,
      });
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Public: Resolve a standalone task list attachment into LiveSections
// ---------------------------------------------------------------------------

export function resolveStandaloneTaskList(
  taskList: TaskListDefinition,
  attachmentIndex: number,
  additionalDetails: AdditionalDetailsValues,
  customer: TemplateCustomerContext,
  taskListCatalog: TaskListDefinition[] = [],
  taskDropdowns: TaskDropdownDefinition[] = [],
): LiveSection[] {
  const warnings: string[] = [];
  const catalogMap = new Map(taskListCatalog.map(tl => [tl.taskListId, tl]));
  const dropdownMap = buildDropdownMap(taskDropdowns);
  // Seed the visited set with this list's own ID so a section in it
  // can't reference its own task list (direct self-reference cycle).
  const flat = flattenReferences(
    taskList.sections,
    catalogMap,
    new Set([taskList.taskListId]),
    [],
    taskList.taskListId,
    warnings,
  );
  if (warnings.length > 0) {
    // Surface to console rather than throwing — this entry point has
    // no warnings channel today, and a missing nested ref shouldn't
    // break an attachment that mostly resolves.
    warnings.forEach(w => console.warn(`[checklistEngine] ${w}`));
  }
  return flat.map(entry => {
    const section = buildLiveSection(
      entry.def,
      entry.sourceTaskListId,
      additionalDetails,
      customer,
      dropdownMap,
      entry.inheritedShowWhens,
    );
    // Apply composite IDs for duplicate attachments.
    if (attachmentIndex > 0) {
      section.sectionId = compositeId(entry.def.sectionId, attachmentIndex);
      section.tasks = section.tasks.map(t => ({
        ...t,
        taskId: compositeId(t.taskId, attachmentIndex),
      }));
    }
    return section;
  });
}

// ---------------------------------------------------------------------------
// Public: Resolve a TemplateTaskConfig into a LiveChecklist
// ---------------------------------------------------------------------------

export interface ResolveResult {
  checklist: LiveChecklist;
  warnings: string[];
}

export function resolveChecklist(
  config: TemplateTaskConfig,
  taskListCatalog: TaskListDefinition[],
  additionalDetails: AdditionalDetailsValues,
  customer: TemplateCustomerContext,
  taskDropdowns: TaskDropdownDefinition[] = [],
): ResolveResult {
  const warnings: string[] = [];
  const catalogMap = new Map(taskListCatalog.map(tl => [tl.taskListId, tl]));
  const dropdownMap = buildDropdownMap(taskDropdowns);
  const flat = flattenReferences(
    config.sections,
    catalogMap,
    new Set(),
    [],
    undefined,
    warnings,
  );
  const sections = flat.map(entry =>
    buildLiveSection(
      entry.def,
      entry.sourceTaskListId,
      additionalDetails,
      customer,
      dropdownMap,
      entry.inheritedShowWhens,
    ),
  );
  return { checklist: { sections }, warnings };
}

// ---------------------------------------------------------------------------
// Public: Re-evaluate visibility on existing checklist
// ---------------------------------------------------------------------------

export function reEvaluateVisibility(
  checklist: LiveChecklist,
  additionalDetails: AdditionalDetailsValues,
  customer: TemplateCustomerContext,
): LiveChecklist {
  let changed = false;

  // Snapshot current outcomes/completion up front so `taskOutcome` conditions
  // evaluate against pre-flip state (a single pass; visibility changes here
  // don't alter outcomes, so no cascade is needed).
  const outcomes = buildTaskOutcomeContext(checklist);

  const sections = checklist.sections.map(section => {
    const sectionVisible = evaluateTaskCondition(section.showWhen, additionalDetails, customer, outcomes);

    let tasksChanged = false;
    const tasks = section.tasks.map(task => {
      const taskVisible = evaluateTaskCondition(task.showWhen, additionalDetails, customer, outcomes);
      // Binary model: a hidden task is simply excluded from progress; we
      // no longer auto-mark it 'n-a'. Only visibility flips here.
      if (task.isVisible !== taskVisible) {
        tasksChanged = true;
        return { ...task, isVisible: taskVisible };
      }
      return task;
    });

    if (section.isVisible !== sectionVisible || tasksChanged) {
      changed = true;
      return { ...section, isVisible: sectionVisible, tasks };
    }
    return section;
  });

  return changed ? { sections } : checklist;
}

// ---------------------------------------------------------------------------
// Public: Progress counts
// ---------------------------------------------------------------------------

/**
 * Binary "done" test. A task is done when it's marked completed (checkbox
 * checked / dropdown option chosen) AND — if its note is mandatory — a
 * non-empty note has been recorded. A required-but-empty note keeps the
 * task blocking, exactly like an unchecked task.
 */
export function isTaskDone(task: LiveTask): boolean {
  if (task.status !== 'completed') return false;
  if (task.noteRequired && !task.note.trim()) return false;
  return true;
}

/**
 * The first visible, not-removed task that is not done, in checklist order —
 * where the mobile job view opens (a scanned label lands on the next thing to
 * do). `null` when every task is done or there are none.
 */
export function findNextOpenTask(
  checklist: LiveChecklist | null | undefined,
): { sectionId: string; taskId: string } | null {
  if (!checklist) return null;
  for (const section of checklist.sections) {
    if (section.isRemoved || !section.isVisible) continue;
    for (const task of section.tasks) {
      if (task.isRemoved || !task.isVisible) continue;
      if (!isTaskDone(task)) return { sectionId: section.sectionId, taskId: task.taskId };
    }
  }
  return null;
}

export function computeSectionProgress(section: LiveSection): ChecklistProgress {
  let total = 0;
  let closed = 0;
  let newCount = 0;

  if (section.isRemoved || !section.isVisible) {
    return { total: 0, closed: 0, new: 0, hasBlockingTasks: false };
  }

  for (const task of section.tasks) {
    if (task.isRemoved || !task.isVisible) continue;
    total++;
    if (isTaskDone(task)) {
      closed++;
    } else {
      newCount++;
    }
  }

  return { total, closed, new: newCount, hasBlockingTasks: newCount > 0 };
}

export function computeChecklistProgress(checklist: LiveChecklist): ChecklistProgress {
  let total = 0;
  let closed = 0;
  let newCount = 0;

  for (const section of checklist.sections) {
    const sp = computeSectionProgress(section);
    total += sp.total;
    closed += sp.closed;
    newCount += sp.new;
  }

  return { total, closed, new: newCount, hasBlockingTasks: newCount > 0 };
}

// ---------------------------------------------------------------------------
// Public: Snapshot for save
// ---------------------------------------------------------------------------

export function snapshotChecklist(
  checklist: LiveChecklist,
  snapshotAt: string,
): SavedChecklist {
  const taskConditions: Record<string, TaskFieldCondition> = {};
  const sectionConditions: Record<string, TaskFieldCondition> = {};

  const sections: SavedSection[] = checklist.sections.map(section => {
    if (section.showWhen) {
      sectionConditions[section.sectionId] = section.showWhen;
    }

    const tasks: SavedTask[] = section.tasks.map(task => {
      if (task.showWhen) {
        taskConditions[task.taskId] = task.showWhen;
      }

      return {
        taskId: task.taskId,
        title: task.title,
        ...(task.description && { description: task.description }),
        ...(task.url && { url: task.url }),
        ...(task.groupLabel && { groupLabel: task.groupLabel }),
        status: task.status,
        ...(task.note && { note: task.note }),
        ...(task.noteRequired && { noteRequired: true }),
        ...(task.dropdownId && { dropdownId: task.dropdownId }),
        ...(task.selectedOption && { selectedOption: task.selectedOption }),
        isRemoved: task.isRemoved,
        isHidden: !task.isVisible,
        source: task.source,
      };
    });

    return {
      sectionId: section.sectionId,
      label: section.label,
      ...(section.sourceTaskListId && { sourceTaskListId: section.sourceTaskListId }),
      isRemoved: section.isRemoved,
      isHidden: !section.isVisible,
      tasks,
    };
  });

  const hasConditions = Object.keys(taskConditions).length > 0 || Object.keys(sectionConditions).length > 0;

  return {
    schemaVersion: 1,
    snapshotAt,
    sections,
    ...(hasConditions && {
      conditions: { tasks: taskConditions, sections: sectionConditions },
    }),
  };
}

// ---------------------------------------------------------------------------
// Public: Audit message builder
// ---------------------------------------------------------------------------

export type AuditAction =
  | 'status-change'
  | 'task-add'
  | 'task-remove'
  | 'task-restore'
  | 'section-remove'
  | 'section-restore';

export interface AuditContext {
  taskTitle?: string;
  sectionLabel?: string;
  from?: TaskStatus;
  to?: TaskStatus;
}

export function buildAuditEntry(
  action: AuditAction,
  context: AuditContext,
  userName: string,
): string {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] ${userName}:`;

  switch (action) {
    case 'status-change':
      return `${prefix} Changed "${context.taskTitle}" from ${context.from} to ${context.to}`;
    case 'task-add':
      return `${prefix} Added task "${context.taskTitle}" to "${context.sectionLabel}"`;
    case 'task-remove':
      return `${prefix} Removed task "${context.taskTitle}"`;
    case 'task-restore':
      return `${prefix} Restored task "${context.taskTitle}"`;
    case 'section-remove':
      return `${prefix} Removed section "${context.sectionLabel}"`;
    case 'section-restore':
      return `${prefix} Restored section "${context.sectionLabel}"`;
  }
}

export function buildSavedAudit(entries: string[]): SavedAudit {
  return {
    schemaVersion: 1,
    entries,
  };
}

// ---------------------------------------------------------------------------
// Public: Hydrate a SavedChecklist back into a LiveChecklist
// ---------------------------------------------------------------------------

/**
 * Convert a persisted SavedChecklist back into an editable LiveChecklist.
 * Used on the Work Item Dashboard to resume editing an existing checklist.
 *
 * Mapping: isHidden → !isVisible, restores showWhen from conditions map.
 */
export function hydrateChecklist(
  saved: SavedChecklist,
  taskDropdowns: TaskDropdownDefinition[] = [],
): LiveChecklist {
  const taskConditions = saved.conditions?.tasks ?? {};
  const sectionConditions = saved.conditions?.sections ?? {};
  const dropdownMap = buildDropdownMap(taskDropdowns);

  const sections: LiveSection[] = saved.sections.map(savedSection => {
    const tasks: LiveTask[] = savedSection.tasks.map(savedTask => ({
      taskId: savedTask.taskId,
      title: savedTask.title,
      description: savedTask.description,
      url: savedTask.url,
      groupLabel: savedTask.groupLabel,
      // Binary status: anything that isn't the (new) 'new' value —
      // including any legacy closed status like 'n-a' — hydrates as done.
      status: savedTask.status === 'new' ? 'new' : 'completed',
      note: savedTask.note ?? '',
      noteRequired: savedTask.noteRequired,
      dropdownId: savedTask.dropdownId,
      options: savedTask.dropdownId
        ? (dropdownMap.get(savedTask.dropdownId) ?? [])
        : undefined,
      selectedOption: savedTask.selectedOption,
      isRemoved: savedTask.isRemoved,
      isVisible: !savedTask.isHidden,
      showWhen: taskConditions[savedTask.taskId],
      source: savedTask.source,
    }));

    return {
      sectionId: savedSection.sectionId,
      label: savedSection.label,
      sourceTaskListId: savedSection.sourceTaskListId,
      isRemoved: savedSection.isRemoved,
      isVisible: !savedSection.isHidden,
      showWhen: sectionConditions[savedSection.sectionId],
      tasks,
    };
  });

  return { sections };
}
